import { UploadedFileEntry } from '@interfaces/mfe/mfe-document-upload.interface';
import { InvestmentObjectives } from '@interfaces/models/investment-objectives-types';
import { VgEmploymentState } from '@interfaces/models/open-account-mfes/employment-mfe.model';
import { PersonalAddressData } from '@interfaces/models/open-account-mfes/personal-address-mfe.model';
import { createSelector } from '@ngrx/store';
import { ClearData, TransactionTypesEnum } from '@shared/models';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { getMfePersonalAddressState } from '@store/reducers';
import { FinancialDetailsState } from '@store/reducers/income-net-worth/income-net-worth.reducer';
import { ILetterUploadState } from '@store/reducers/letter-upload/letter-upload.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { MfePersonalAddressState } from '@store/reducers/personal-info-address/personal-info-address.reducers';
import { MfePhoneState } from '@store/reducers/personal-phone/personal-phone.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import {
  selectClearData,
  selectWldLetterDocuments,
} from '@store/selectors/document-upload/document-upload.selectors';
import { selectItMultiReceivingToggle } from '@store/selectors/feature/feature.selectors';
import { selectIncomeNetWorth } from '@store/selectors/income-net-worth/income-net-worth.selectors';
import { selectInternalTransferTransactionType } from '@store/selectors/internal-transfer/internal-transfer.selectors';
import { selectInvestmentObjectives } from '@store/selectors/investment-objectives/investment-objectives.selectors';
import { selectLetterUploadState } from '@store/selectors/letter-upload/letter-upload.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectMfePersonalInfoClientSSN } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';
import { selectTransactionType } from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import {
  selectCurrentTransaction,
  selectIsNewAccountTransactionExist,
} from '@store/selectors/transaction-state/transaction-state.selectors';
import { selectTriageState } from '@store/selectors/triage/triage.selectors';
import {
  selectEmploymentFeature,
  selectFinancialsState,
  selectInvestmentObjectivesState,
  selectIsPersonalInfoSSN,
} from 'vg-interaction-model';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';

import { selectMfePhone } from '../../personal-phone/personal-phone.selectors';
import {
  buildEmploymentReceipt,
  buildFinancialProfileReceipt,
  buildProfileReceipt,
} from './new-account.cashier';

export const selectProfileReceipt = (
  editable: boolean,
  content?: typeof ReviewContent.receiptContent.profile,
) =>
  createSelector(
    selectTriageState,
    selectTransactionType,
    selectMfePersonalInfoClientPersonalInfo,
    selectMfePersonalInfoClientSSN,
    getMfePersonalAddressState,
    selectMfePhone,
    selectClearData,
    selectIsNewAccountTransactionExist,
    selectCurrentTransaction,
    (
      guideState: TriageState,
      transactionType: TransactionTypesEnum | null,
      personalInfo: MfePersonalInfoClientState['personalInfo'],
      ssn_private: string,
      address: MfePersonalAddressState,
      phone: MfePhoneState,
      clearData: ClearData,
      isNewAccountTransaction: boolean,
      currentTransaction,
    ) => {
      const transactionWithExecutorInfo = !!currentTransaction?.estate?.estateName;
      const transactionWithTrusteeInfo = !!currentTransaction?.trust?.trustName;
      return buildProfileReceipt(
        guideState,
        transactionType,
        personalInfo,
        ssn_private,
        address,
        phone,
        clearData,
        isNewAccountTransaction,
        editable,
        content,
        transactionWithExecutorInfo,
        transactionWithTrusteeInfo,
      );
    },
  );

export const selectEmploymentReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.employment,
) =>
  createSelector(
    selectEmploymentMfeDataByOwnerIndex(0),
    selectTransactionType,
    selectInternalTransferTransactionType,
    selectTriageState,
    selectLetterUploadState,
    selectWldLetterDocuments,
    (
      state: Partial<VgEmploymentState> | undefined,
      transactionType: TransactionTypesEnum | null,
      internalTransferTransactionType: TransactionTypesEnum | null,
      triageState: TriageState,
      letterUploadState: ILetterUploadState,
      wldLetterDocuments: UploadedFileEntry[],
    ) =>
      buildEmploymentReceipt(
        state,
        transactionType as TransactionTypesEnum,
        internalTransferTransactionType as TransactionTypesEnum,
        triageState,
        letterUploadState,
        editable,
        content,
        wldLetterDocuments,
      ),
  );

export const selectFinancialProfileReceipt = (
  editable: boolean = true,
  content?: typeof ReviewContent.receiptContent.financialProfile,
) =>
  createSelector(
    selectIncomeNetWorth,
    selectInvestmentObjectives,
    selectTransactionType,
    selectInternalTransferTransactionType,
    selectTriageState,
    selectItMultiReceivingToggle,
    (
      financial: FinancialDetailsState,
      invObj: { objectives: InvestmentObjectives },
      transactionType: TransactionTypesEnum | null,
      internalTransferTransactionType: TransactionTypesEnum | null,
      triageState: TriageState,
      isItMultiReceivingToggle: boolean,
    ) => {
      return buildFinancialProfileReceipt(
        financial,
        invObj,
        editable,
        transactionType,
        internalTransferTransactionType,
        triageState,
        content,
        isItMultiReceivingToggle,
      );
    },
  );
